// src/services/projectService.js
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  Timestamp,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";

// Project status
export const PROJECT_STATUS = {
  PLANNING: "planning",
  IN_PROGRESS: "in_progress",
  REVIEW: "review",
  COMPLETED: "completed",
  ON_HOLD: "on_hold",
  CANCELLED: "cancelled",
};

// Project priority
export const PROJECT_PRIORITY = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical",
};

const PROJECTS_COLLECTION = "projects";

// Helper function to safely convert dates
const safeToDate = (dateValue) => {
  // Handle null, undefined, empty string
  if (
    !dateValue ||
    dateValue === "" ||
    dateValue === null ||
    dateValue === undefined
  ) {
    return null;
  }

  // If it's already a Date object
  if (dateValue instanceof Date) {
    return dateValue;
  }

  // If it's a Firestore Timestamp
  if (dateValue && typeof dateValue.toDate === "function") {
    return dateValue.toDate();
  }

  // If it's a number (timestamp)
  if (typeof dateValue === "number") {
    return new Date(dateValue);
  }

  // If it's a Firestore Timestamp object with seconds
  if (dateValue && typeof dateValue.seconds === "number") {
    return new Date(dateValue.seconds * 1000);
  }

  // Handle string dates (from forms)
  if (typeof dateValue === "string" && dateValue.trim() !== "") {
    const parsed = new Date(dateValue);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

// Create a new project
export const createProject = async (projectData) => {
  try {
    const project = {
      ...projectData,
      status: projectData.status || PROJECT_STATUS.PLANNING,
      priority: projectData.priority || PROJECT_PRIORITY.MEDIUM,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      assignedStudents: projectData.assignedStudents || [],
      tasks: [], // Array to store task IDs
      progress: 0, // Completion percentage
    };

    // Handle date fields for new projects
    if (projectData.startDate && projectData.startDate.trim() !== "") {
      project.startDate = Timestamp.fromDate(new Date(projectData.startDate));
    }
    if (projectData.endDate && projectData.endDate.trim() !== "") {
      project.endDate = Timestamp.fromDate(new Date(projectData.endDate));
    }

    const docRef = await addDoc(collection(db, PROJECTS_COLLECTION), project);

    toast.success("Project created successfully!");
    return { id: docRef.id, ...project };
  } catch (error) {
    console.error("Error creating project:", error);
    toast.error("Failed to create project");
    throw error;
  }
};

// Get all projects
export const getProjects = async () => {
  try {
    const q = query(
      collection(db, PROJECTS_COLLECTION),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    const projects = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      projects.push({
        id: doc.id,
        ...data,
        createdAt: safeToDate(data.createdAt),
        updatedAt: safeToDate(data.updatedAt),
        startDate: safeToDate(data.startDate),
        endDate: safeToDate(data.endDate),
        dueDate: safeToDate(data.dueDate),
      });
    });

    return projects;
  } catch (error) {
    console.error("Error fetching projects:", error);
    toast.error("Failed to fetch projects");
    return [];
  }
};

// Get projects by student ID
export const getProjectsByStudentId = async (studentId) => {
  try {
    const q = query(
      collection(db, PROJECTS_COLLECTION),
      where("assignedStudents", "array-contains", studentId),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    const projects = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      projects.push({
        id: doc.id,
        ...data,
        createdAt: safeToDate(data.createdAt),
        updatedAt: safeToDate(data.updatedAt),
        startDate: safeToDate(data.startDate),
        endDate: safeToDate(data.endDate),
        dueDate: safeToDate(data.dueDate),
      });
    });

    return projects;
  } catch (error) {
    console.error("Error fetching student projects:", error);
    toast.error("Failed to fetch projects");
    return [];
  }
};

// Get project by ID
export const getProjectById = async (projectId) => {
  try {
    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);
    const projectSnap = await getDoc(projectRef);

    if (projectSnap.exists()) {
      const data = projectSnap.data();
      return {
        id: projectSnap.id,
        ...data,
        createdAt: safeToDate(data.createdAt),
        updatedAt: safeToDate(data.updatedAt),
        startDate: safeToDate(data.startDate),
        endDate: safeToDate(data.endDate),
        dueDate: safeToDate(data.dueDate),
      };
    }

    return null;
  } catch (error) {
    console.error("Error fetching project:", error);
    toast.error("Failed to fetch project details");
    return null;
  }
};

// Update project
export const updateProject = async (projectId, updateData) => {
  try {
    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);

    const dataToUpdate = {
      ...updateData,
      updatedAt: Timestamp.now(),
    };

    // Handle date fields
    if (updateData.startDate && updateData.startDate.trim() !== "") {
      dataToUpdate.startDate = Timestamp.fromDate(
        new Date(updateData.startDate)
      );
    } else if (updateData.startDate === "") {
      // If empty string is sent, remove the field
      dataToUpdate.startDate = null;
    }

    if (updateData.endDate && updateData.endDate.trim() !== "") {
      dataToUpdate.endDate = Timestamp.fromDate(new Date(updateData.endDate));
    } else if (updateData.endDate === "") {
      // If empty string is sent, remove the field
      dataToUpdate.endDate = null;
    }

    await updateDoc(projectRef, dataToUpdate);

    toast.success("Project updated successfully!");
    return true;
  } catch (error) {
    console.error("Error updating project:", error);
    toast.error("Failed to update project");
    throw error;
  }
};

// Delete project
export const deleteProject = async (projectId) => {
  try {
    await deleteDoc(doc(db, PROJECTS_COLLECTION, projectId));
    toast.success("Project deleted successfully!");
    return true;
  } catch (error) {
    console.error("Error deleting project:", error);
    toast.error("Failed to delete project");
    throw error;
  }
};

// Assign student to project
export const assignStudentToProject = async (projectId, studentId) => {
  try {
    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);

    await updateDoc(projectRef, {
      assignedStudents: arrayUnion(studentId),
      updatedAt: Timestamp.now(),
    });

    toast.success("Student assigned to project!");
    return true;
  } catch (error) {
    console.error("Error assigning student:", error);
    toast.error("Failed to assign student");
    throw error;
  }
};

// Remove student from project
export const removeStudentFromProject = async (projectId, studentId) => {
  try {
    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);

    await updateDoc(projectRef, {
      assignedStudents: arrayRemove(studentId),
      updatedAt: Timestamp.now(),
    });

    toast.success("Student removed from project!");
    return true;
  } catch (error) {
    console.error("Error removing student:", error);
    toast.error("Failed to remove student");
    throw error;
  }
};

// Add task to project
export const addTaskToProject = async (projectId, taskId) => {
  try {
    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);

    await updateDoc(projectRef, {
      tasks: arrayUnion(taskId),
      updatedAt: Timestamp.now(),
    });

    return true;
  } catch (error) {
    console.error("Error adding task to project:", error);
    throw error;
  }
};

// Remove task from project
export const removeTaskFromProject = async (projectId, taskId) => {
  try {
    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);

    await updateDoc(projectRef, {
      tasks: arrayRemove(taskId),
      updatedAt: Timestamp.now(),
    });

    return true;
  } catch (error) {
    console.error("Error removing task from project:", error);
    throw error;
  }
};

// Update project progress based on completed tasks
export const updateProjectProgress = async (projectId) => {
  try {
    // This would need to be called after task status changes
    // We'll implement this when we update the task service
    const project = await getProjectById(projectId);

    if (!project || !project.tasks || project.tasks.length === 0) {
      return;
    }

    // Calculate progress based on completed tasks
    // This is a placeholder - we'll implement the actual calculation
    // when we have the task service integration

    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);
    await updateDoc(projectRef, {
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error updating project progress:", error);
  }
};

// Get project statistics
export const getProjectStats = async () => {
  try {
    const projects = await getProjects();

    const stats = {
      total: projects.length,
      planning: projects.filter((p) => p.status === PROJECT_STATUS.PLANNING)
        .length,
      inProgress: projects.filter(
        (p) => p.status === PROJECT_STATUS.IN_PROGRESS
      ).length,
      review: projects.filter((p) => p.status === PROJECT_STATUS.REVIEW).length,
      completed: projects.filter((p) => p.status === PROJECT_STATUS.COMPLETED)
        .length,
      onHold: projects.filter((p) => p.status === PROJECT_STATUS.ON_HOLD)
        .length,
      cancelled: projects.filter((p) => p.status === PROJECT_STATUS.CANCELLED)
        .length,
    };

    return stats;
  } catch (error) {
    console.error("Error calculating project stats:", error);
    return null;
  }
};

// Get project status badge configuration
export const getProjectStatusConfig = (status) => {
  const statusConfigs = {
    [PROJECT_STATUS.PLANNING]: {
      bg: "secondary",
      text: "Planning",
      icon: "📋",
    },
    [PROJECT_STATUS.IN_PROGRESS]: {
      bg: "primary",
      text: "In Progress",
      icon: "🚀",
    },
    [PROJECT_STATUS.REVIEW]: { bg: "warning", text: "Review", icon: "👀" },
    [PROJECT_STATUS.COMPLETED]: {
      bg: "success",
      text: "Completed",
      icon: "✅",
    },
    [PROJECT_STATUS.ON_HOLD]: { bg: "info", text: "On Hold", icon: "⏸️" },
    [PROJECT_STATUS.CANCELLED]: { bg: "danger", text: "Cancelled", icon: "❌" },
  };

  return statusConfigs[status] || { bg: "secondary", text: status, icon: "📋" };
};

// Get project priority badge configuration
export const getProjectPriorityConfig = (priority) => {
  const priorityConfigs = {
    [PROJECT_PRIORITY.LOW]: { bg: "success", text: "Low", icon: "🟢" },
    [PROJECT_PRIORITY.MEDIUM]: { bg: "warning", text: "Medium", icon: "🟡" },
    [PROJECT_PRIORITY.HIGH]: { bg: "danger", text: "High", icon: "🔴" },
    [PROJECT_PRIORITY.CRITICAL]: { bg: "dark", text: "Critical", icon: "🚨" },
  };

  return (
    priorityConfigs[priority] || { bg: "secondary", text: priority, icon: "🟢" }
  );
};
